import React, { useState, useEffect } from 'react';
import { Card, Badge, List, Typography, Divider, Tooltip } from 'antd';
import { UserOutlined, CoffeeOutlined, RestOutlined, LoginOutlined } from '@ant-design/icons';
import { getCurrentShiftEmployees } from '../services/breakService';
import websocketService from '../services/websocket';

const { Title, Text } = Typography;

const EmployeeStatusPanel = ({ onToggle }) => {
  const [employees, setEmployees] = useState({
    Ready: [],
    Break: [],
    LunchBreak: [],
    Dayoff: []
  });
  const [lunchBreakStatus, setLunchBreakStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentShift, setCurrentShift] = useState(null);
  const [currentTime, setCurrentTime] = useState('');

  // ดึงข้อมูลพนักงาน
  const fetchEmployees = async () => {
    try {
      setIsLoading(true);
      const response = await getCurrentShiftEmployees();
      if (response.success) {
        setEmployees(response.result.employees);
        setLunchBreakStatus(response.result.lunchBreakStatus);
        setCurrentShift(response.result.currentShift);
        setCurrentTime(response.result.currentTime);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
    
    // Setup WebSocket listeners
    const handleBreakEvent = (eventData) => {
      // รีเฟรชข้อมูลเมื่อมีการเปลี่ยนแปลงสถานะ
      fetchEmployees();
    };

    const handleWorkEvent = (eventData) => {
      // รีเฟรชข้อมูลเมื่อมีการเข้า/ออกงาน
      fetchEmployees();
    };

    websocketService.onBreakEvent(handleBreakEvent);
    websocketService.onWorkEvent(handleWorkEvent);

    return () => {
      // Cleanup listeners
    };
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Ready':
        return <UserOutlined style={{ color: '#52c41a' }} />;
      case 'Break':
        return <CoffeeOutlined style={{ color: '#faad14' }} />;
      case 'LunchBreak':
        return <RestOutlined style={{ color: '#ff7a45' }} />;
      case 'Dayoff':
        return <LoginOutlined style={{ color: '#d9d9d9' }} />;
      default:
        return <UserOutlined style={{ color: '#d9d9d9' }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Ready':
        return 'green';
      case 'Break':
        return 'orange';
      case 'LunchBreak':
        return 'red';
      case 'Dayoff':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'Ready':
        return 'ออนไลน์';
      case 'Break':
        return 'พักเบรค';
      case 'LunchBreak':
        return 'พักกินข้าว';
      case 'Dayoff':
        return 'ออฟไลน์';
      default:
        return 'ไม่ทราบสถานะ';
    }
  };

  const renderEmployeeList = (employeeList, status) => {
    if (employeeList.length === 0) {
      return (
        <div style={{ padding: '8px 0', color: '#999' }}>
          <Text type="secondary">ไม่มีพนักงาน</Text>
        </div>
      );
    }

    return (
      <List
        size="small"
        dataSource={employeeList}
        renderItem={(employee) => (
          <List.Item style={{ padding: '4px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {getStatusIcon(status)}
              <Text>{employee.UserName}</Text>
              <Badge 
                count={`${employee.JobType == '1' ? 'Input' : employee.JobType == '2' ? 'Output' : ''}`}
                style={{ 
                  backgroundColor: employee.JobType == '1' ? '#52c41a' : employee.JobType == '2' ? '#1890ff' : '#722ed1',
                  fontSize: '10px'
                }}
              />
            </div>
          </List.Item>
        )}
      />
    );
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Title level={5} style={{ margin: 0 }}>สถานะพนักงาน</Title>
          <div style={{ textAlign: 'right' }}>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
              {currentShift === '2' ? 'กะเช้า' : currentShift === '3' ? 'กะกลางคืน' : 'ไม่ทราบกะ'}
            </Text>
            <Text type="secondary" style={{ fontSize: '10px' }}>
              {currentTime}
            </Text>
          </div>
        </div>
      }
      size="small"
      style={{ 
        width: 300, 
        maxHeight: 400, 
        overflow: 'hidden',
        position: 'absolute',
        top: '100%',
        left: 0,
        zIndex: 1000,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
      }}
      loading={isLoading}
    >
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        {/* พร้อมทำงาน */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Badge color={getStatusColor('Ready')} />
            <Text strong>{getStatusText('Ready')} ({employees.Ready.length})</Text>
          </div>
          {renderEmployeeList(employees.Ready, 'Ready')}
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {/* พักกินข้าว */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Badge color={getStatusColor('LunchBreak')} />
            <Text strong>{getStatusText('LunchBreak')} ({employees.LunchBreak.length})</Text>
          </div>
          {renderEmployeeList(employees.LunchBreak, 'LunchBreak')}
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {/* พักเบรค */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Badge color={getStatusColor('Break')} />
            <Text strong>{getStatusText('Break')} ({employees.Break.length})</Text>
          </div>
          {renderEmployeeList(employees.Break, 'Break')}
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {/* หยุดงาน */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Badge color={getStatusColor('Dayoff')} />
            <Text strong>{getStatusText('Dayoff')} ({employees.Dayoff.length})</Text>
          </div>
          {renderEmployeeList(employees.Dayoff, 'Dayoff')}
        </div>
      </div>
    </Card>
  );
};

export default EmployeeStatusPanel; 